/**
 * Loads additional catalog / search listing pages via Section Rendering API.
 * Supports collection + search products (ul.grid), search articles (.blog-list), search pages (#page-grid ul).
 */
(function () {
  'use strict';

  const OBSERVER_OPTS = {
    root: null,
    rootMargin: '0px 0px 420px 0px',
    threshold: 0,
  };

  /** @type {WeakMap<Element, IntersectionObserver>} */
  const observers = new WeakMap();

  /** @type {WeakMap<Element, boolean>} */
  const busyStates = new WeakMap();

  let gridGeneration = 0;

  function teardown(container) {
    const obs = observers.get(container);
    if (obs) {
      obs.disconnect();
      observers.delete(container);
    }
  }

  /**
   * Merges live URL bar / history params (filters, search, sort) into the `paginate.next` request URL.
   * `page` stays from paginate.next; `page` from the visible URL is ignored while fetching older chunks.
   */
  function isListingFacetKeyDecoded(keyDecoded) {
    const kLower = keyDecoded.toLowerCase();
    if (kLower === 'sort_by') return true;
    if (kLower === 'options[prefix]' || kLower.startsWith('options[')) return true;
    if (kLower === 'constraint' || kLower.startsWith('constraint.')) return true;
    if (kLower.startsWith('filter_')) return true;
    if (kLower.startsWith('filter.')) return true;
    return false;
  }

  /** Search template query keys layered on storefront filter semantics. */
  function isListingSearchFacetKeyDecoded(keyDecoded) {
    const kLower = keyDecoded.toLowerCase();
    if (kLower === 'q' || kLower === 'type') return true;
    return isListingFacetKeyDecoded(keyDecoded);
  }

  function purgeMergedListingParams(mergedSearchParams, isSearchPagePath) {
    const encKeys = [...new Set(Array.from(mergedSearchParams.keys()))];
    for (const enc of encKeys) {
      let dk;
      try {
        dk = decodeURIComponent(enc.replace(/\+/g, '%20'));
      } catch {
        dk = enc;
      }
      const low = dk.toLowerCase();
      if (low === 'page' || low === 'section_id') continue;
      const Pred = isSearchPagePath ? isListingSearchFacetKeyDecoded : isListingFacetKeyDecoded;
      if (!Pred(dk)) continue;
      mergedSearchParams.delete(enc);
    }
  }

  function mergeActiveListingParams(targetUrl, sectionId, isSearchPagePath) {
    const merged = new URL(targetUrl.toString());
    const nextPageNum = merged.searchParams.get('page') || '1';

    const live = window.location.search
      ? new URLSearchParams(window.location.search.replace(/^\?/, ''))
      : new URLSearchParams();

    purgeMergedListingParams(merged.searchParams, isSearchPagePath);
    merged.searchParams.delete('page');
    merged.searchParams.delete('section_id');

    const seenLiveEncoded = new Set();
    const liveEncKeysOrdered = [...new URLSearchParams(live.toString()).keys()];

    for (const enc of liveEncKeysOrdered) {
      if (seenLiveEncoded.has(enc)) continue;
      seenLiveEncoded.add(enc);
      let dk;
      try {
        dk = decodeURIComponent(enc.replace(/\+/g, '%20'));
      } catch {
        dk = enc;
      }
      if (dk.toLowerCase() === 'page') continue;
      const Pred = isSearchPagePath ? isListingSearchFacetKeyDecoded : isListingFacetKeyDecoded;
      if (!Pred(dk)) continue;
      live.getAll(enc).forEach((val) => {
        merged.searchParams.append(enc, val);
      });
    }

    merged.searchParams.set('page', nextPageNum);
    merged.searchParams.set('section_id', sectionId);
    return merged;
  }

  /** True when pathname is storefront search (/search, incl. localized prefixes before /search). */
  function listingPathEndsWithSearch() {
    const p = window.location.pathname.replace(/\/+$/, '');
    return /\/search$/i.test(p);
  }

  /**
   * @param {boolean} searchPath — use search-aware query-merge (type, q)
   */
  function buildFetchUrl(linkUrlRaw, sectionId, searchPath) {
    const base = `${window.location.origin}${window.location.pathname}`;
    const linkUrl = typeof linkUrlRaw === 'string' ? linkUrlRaw.trim() : String(linkUrlRaw);

    /** @type {URL} */
    let u;
    if (/^https?:\/\//i.test(linkUrl)) {
      u = new URL(linkUrl);
    } else {
      /** Relative next links resolve against current pathname + locale. */
      u = new URL(linkUrl.startsWith('?') || linkUrl === '' ? linkUrl || '?' : linkUrl, base);
    }

    return mergeActiveListingParams(u, sectionId, searchPath).toString();
  }

  function setFooterLoading(footerEl, loading) {
    if (!footerEl?.isConnected) return;
    const inner = footerEl.querySelector('[data-collection-infinite-loading]');
    if (!inner) return;
    inner.classList.toggle('visually-hidden', !loading);
    const spinner = inner.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !loading);
    const sentinel = footerEl.querySelector('[data-collection-infinite-sentinel]');
    sentinel?.setAttribute('aria-busy', loading ? 'true' : 'false');
  }

  function syncContainerInfiniteAttrs(domContainer, sourceContainer) {
    const nextRaw = sourceContainer.getAttribute('data-collection-infinite-next-url') || '';
    if (nextRaw) {
      domContainer.setAttribute('data-collection-infinite-next-url', nextRaw);
    } else {
      domContainer.removeAttribute('data-collection-infinite-next-url');
    }

    const srcInfinite = sourceContainer.getAttribute('data-collection-infinite-scroll') === 'true';
    if (srcInfinite) {
      domContainer.setAttribute('data-collection-infinite-scroll', 'true');
    } else {
      domContainer.removeAttribute('data-collection-infinite-scroll');
    }
  }

  /**
   * #product-grid is the <ul> on collection pages, or a wrapper <div> on search products (inner <ul> holds cards).
   */
  function resolveProductListAndFooterScope(productGridRoot) {
    if (!productGridRoot || productGridRoot.closest('.collection--empty')) return null;

    let listEl;
    if (productGridRoot.matches('ul')) {
      listEl = productGridRoot;
    } else {
      listEl = productGridRoot.querySelector(':scope > ul.grid, :scope > ul.product-grid');
    }
    if (!listEl) return null;

    const footerScope = productGridRoot.closest('.collection');
    const sectionId = productGridRoot.dataset.id;
    if (!footerScope || !sectionId) return null;

    return { listEl, footerScope, sectionId, itemSelector: ':scope > li', anchorAfterEl: listEl };
  }

  function resolveArticleSearchContext(articleGrid) {
    if (!articleGrid) return null;
    const listEl = articleGrid.querySelector(':scope .blog-list');
    const blogListSection = articleGrid.querySelector(':scope > .blog-list-section');
    if (!listEl || !blogListSection) return null;
    const footerScope = articleGrid.closest('.collection');
    const sectionId = articleGrid.dataset.id;
    if (!footerScope || !sectionId) return null;
    return {
      listEl,
      footerScope,
      sectionId,
      itemSelector: ':scope > .blog-list-item',
      anchorAfterEl: blogListSection,
    };
  }

  function resolvePageSearchContext(pageGrid) {
    if (!pageGrid) return null;
    const listEl = pageGrid.querySelector(':scope > ul');
    if (!listEl) return null;
    const footerScope = pageGrid.closest('.collection');
    const sectionId = pageGrid.dataset.id;
    if (!footerScope || !sectionId) return null;
    return { listEl, footerScope, sectionId, itemSelector: ':scope > li', anchorAfterEl: listEl };
  }

  /**
   * @param {Document|DocumentFragment|ParentNode} doc
   */
  function resolveInfiniteContext(doc) {
    const productRoot = doc.getElementById('product-grid');
    if (productRoot) {
      const resolved = resolveProductListAndFooterScope(productRoot);
      if (resolved) return resolved;
    }
    const articleResolved = resolveArticleSearchContext(doc.getElementById('article-grid'));
    if (articleResolved) return articleResolved;
    const pageResolved = resolvePageSearchContext(doc.getElementById('page-grid'));
    if (pageResolved) return pageResolved;
    return null;
  }

  function observeFooter(container, context, sentinelEl) {
    const { listEl, footerScope, sectionId, anchorAfterEl } = context;
    const footerInsertAfter = anchorAfterEl || listEl;
    teardown(container);

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const nextUrlRaw =
          container.getAttribute('data-collection-infinite-next-url') ||
          container.dataset.collectionInfiniteNextUrl ||
          '';
        if (!nextUrlRaw) {
          teardown(container);
          footerScope.querySelector('[data-collection-infinite-footer]')?.remove();
          return;
        }

        const currentFooter = footerScope.querySelector('[data-collection-infinite-footer]');
        if (!currentFooter?.isConnected || busyStates.get(container)) return;

        busyStates.set(container, true);
        setFooterLoading(currentFooter, true);

        const fetchUrl = buildFetchUrl(nextUrlRaw, sectionId, listingPathEndsWithSearch());
        const fetchGeneration = gridGeneration;

        fetch(fetchUrl)
          .then((r) => r.text())
          .then((responseText) => {
            if (fetchGeneration !== gridGeneration) return;

            const doc = new DOMParser().parseFromString(responseText, 'text/html');
            const fetchedContainer = doc.getElementById('ProductGridContainer');
            const fetchedResolved = resolveInfiniteContext(doc);

            if (!fetchedContainer || !fetchedResolved) return;

            const items = fetchedResolved.listEl.querySelectorAll(fetchedResolved.itemSelector);
            items.forEach((node) => {
              listEl.appendChild(node);
            });

            if (
              window.CollectionPricing &&
              typeof window.CollectionPricing.scan === 'function'
            ) {
              window.CollectionPricing.scan();
            }

            container.querySelectorAll('.scroll-trigger').forEach((element) => {
              element.classList.add('scroll-trigger--cancel');
            });

            syncContainerInfiniteAttrs(container, fetchedContainer);
            currentFooter.remove();

            const newNextRaw = fetchedContainer.getAttribute('data-collection-infinite-next-url');
            const newFooter = fetchedContainer.querySelector('[data-collection-infinite-footer]');

            if (
              newNextRaw &&
              newFooter &&
              footerScope.classList.contains('collection')
            ) {
              footerInsertAfter.insertAdjacentHTML('afterend', newFooter.outerHTML);
              initInfiniteScroll();
            } else if (!newNextRaw) {
              container.removeAttribute('data-collection-infinite-next-url');
              teardown(container);
            }
          })
          .catch(() => {})
          .finally(() => {
            busyStates.delete(container);
            const liveFooter =
              document.getElementById('ProductGridContainer')?.querySelector(
                '[data-collection-infinite-footer]'
              ) || undefined;
            setFooterLoading(liveFooter, false);
          });
      });
    }, OBSERVER_OPTS);

    observers.set(container, observer);
    observer.observe(sentinelEl);
  }

  function initInfiniteScroll() {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    teardown(container);

    if (container.dataset.collectionInfiniteScroll !== 'true') return;

    const resolved = resolveInfiniteContext(document);
    if (!resolved) return;

    const { footerScope } = resolved;
    const footerEl = footerScope.querySelector('[data-collection-infinite-footer]');
    const sentinelEl = footerEl?.querySelector('[data-collection-infinite-sentinel]');
    const nextUrl =
      container.getAttribute('data-collection-infinite-next-url') ||
      container.dataset.collectionInfiniteNextUrl ||
      '';

    if (!footerEl || !sentinelEl || !nextUrl) return;

    observeFooter(container, resolved, sentinelEl);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInfiniteScroll);
  } else {
    initInfiniteScroll();
  }

  window.addEventListener('product-grid:updated', () => {
    gridGeneration += 1;
    initInfiniteScroll();
  });

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', initInfiniteScroll);
  }
})();
